import {
  BehaviorSubject,
  merge,
  fromEvent,
  animationFrameScheduler,
} from "rxjs";
import {
  map,
  merge as mergeO,
  withLatestFrom,
  scan,
  startWith,
  shareReplay,
  switchMap,
  distinctUntilChanged,
  auditTime,
} from "rxjs/operators";
import { get, isNumber, isFunction } from "lodash";

const extractPropsFromMedia = (element) => ({
  currentTime: element.currentTime * 1000,
  duration: element.duration * 1000,
  paused: element.paused,
  readyState: element.readyState,
  volume: element.volume,
  playbackRate: element.playbackRate,
});

const observePropsOnEvents = (element) => {
  const slowEvents$ = merge(
    fromEvent(element, "abort"),
    fromEvent(element, "canplay"),
    fromEvent(element, "canplaythrough"),
    fromEvent(element, "durationchange"),
    fromEvent(element, "emptied"),
    fromEvent(element, "ended"),
    fromEvent(element, "error"),
    fromEvent(element, "loadeddata"),
    fromEvent(element, "loadedmetadata"),
    fromEvent(element, "loadstart"),
    fromEvent(element, "playing"),
    fromEvent(element, "progress"),
    fromEvent(element, "ratechange"),
    fromEvent(element, "seeking"),
    fromEvent(element, "stalled"),
    fromEvent(element, "suspend"),
    fromEvent(element, "timeupdate"),
    fromEvent(element, "volumechange"),
    fromEvent(element, "waiting"),
    fromEvent(element, "speedchange")
  ).pipe(auditTime(50, animationFrameScheduler));

  const fastEvents$ = merge(
    fromEvent(element, "seeked"),
    fromEvent(element, "pause"),
    fromEvent(element, "play"),
    fromEvent(element, "playbackRate")
  ).pipe(auditTime(0, animationFrameScheduler));

  return merge(slowEvents$, fastEvents$).pipe(
    map((event) => extractPropsFromMedia(event.target)),
    startWith(extractPropsFromMedia(element))
  );
};

const MediaStore = (mediaNode$) => {
  const mediaUserEvents$ = new BehaviorSubject();
  const nodeEvents$ = mediaNode$.pipe(
    switchMap((elem) => observePropsOnEvents(elem)),
    map((props) => ({ __id: "RECEIVE_EVENT_MEDIA", props }))
  );
  const $ = merge(mediaUserEvents$, nodeEvents$).pipe(
    withLatestFrom(mediaNode$),
    scan((state, [action = {}, domNode]) => {
      switch (action.__id) {
        case "PLAY_MEDIA":
          domNode.play();
          return state;
        case "PAUSE_MEDIA":
          domNode.pause();
          return state;
        case "SKIP_PREV_NEXT_HIT":
          state.skipPrevNextHit = action.status;
          return { ...state };
        case "SEEK_MEDIA":
          const { s = null, p = null } = action.time;
          if (isNumber(s)) {
            state.currentTime = s;
            domNode.currentTime = s / 1000;
          }

          // if (isNumber(p)) {
          state.currentPosition = p;
          // }

          return { ...state };
        case "SET_VOLUME_MEDIA":
          domNode.volume = action.volume;
          return state;
        case "RECEIVE_EVENT_MEDIA":
          return {
            ...state,
            ...action.props,
          };
        case "SET_SENTIMENT_ANALYZER":
          return {
            ...state,
            isSentimentAnalyzerOn: action.isSentimentAnalyzerOn,
          };
        case "SET_KEYWORD_ANALYZER":
          return {
            ...state,
            isKeywordAnalyzerOn: action.isKeywordAnalyzerOn,
          };
        case "SET_PHRASE_SPORTING_ANALYZER":
          return {
            ...state,
            isPhraseSupportingAnalyzerOn: action.isPhraseSupportingAnalyzerOn,
          };
        case "SET_VERB_NOUN_ANALYZER":
          return {
            ...state,
            isVerbNounAnalyzerOn: action.isVerbNounAnalyzerOn,
          };
        case "SET_ENTITIES_ANALYZER":
          return {
            ...state,
            isEntitiesAnalyzerOn: action.isEntitiesAnalyzerOn,
          };
        case "SET_PLAYBACK_SPEED":
          domNode.playbackRate = action.playbackRate;
          return {
            ...state,
            playbackRate: action.playbackRate,
          };
        case "SET_INTERACTION_TYPE":
          return {
            ...state,
            interactionType: action.type,
          };

        default:
          return state;
      }
    }, {}),
    distinctUntilChanged(),
    shareReplay(1)
  );
  return {
    $,
    destroy: () => {
      [mediaUserEvents$, nodeEvents$, $].forEach((store) => {
        if (isFunction(get(store, "complete"))) store.complete();
      });
    },
    actions: {
      playMedia: () =>
        mediaUserEvents$.next({
          __id: "PLAY_MEDIA",
        }),
      pauseMedia: () =>
        mediaUserEvents$.next({
          __id: "PAUSE_MEDIA",
        }),
      seekMedia: (time) =>
        mediaUserEvents$.next({
          __id: "SEEK_MEDIA",
          time,
        }),
      skipPrevNextHit: (status) =>
        mediaUserEvents$.next({
          __id: "SKIP_PREV_NEXT_HIT",
          status,
        }),
      setVolumeMedia: (volume) =>
        mediaUserEvents$.next({
          __id: "SET_VOLUME_MEDIA",
          volume,
        }),
      setSentimentAnalyzer: (isSentimentAnalyzerOn) =>
        mediaUserEvents$.next({
          __id: "SET_SENTIMENT_ANALYZER",
          isSentimentAnalyzerOn,
        }),
      setKeywordAnalyzer: (isKeywordAnalyzerOn) =>
        mediaUserEvents$.next({
          __id: "SET_KEYWORD_ANALYZER",
          isKeywordAnalyzerOn,
        }),
      setPhraseSupportingAnalyzer: (isPhraseSupportingAnalyzerOn) =>
        mediaUserEvents$.next({
          __id: "SET_PHRASE_SPORTING_ANALYZER",
          isPhraseSupportingAnalyzerOn,
        }),
      setVerbNounAnalyzer: (isVerbNounAnalyzerOn) =>
        mediaUserEvents$.next({
          __id: "SET_VERB_NOUN_ANALYZER",
          isVerbNounAnalyzerOn,
        }),
      setEntitiesAnalyzer: (isEntitiesAnalyzerOn) =>
        mediaUserEvents$.next({
          __id: "SET_ENTITIES_ANALYZER",
          isEntitiesAnalyzerOn,
        }),
      setPlaybackSpeed: (playbackRate) =>
        mediaUserEvents$.next({
          __id: "SET_PLAYBACK_SPEED",
          playbackRate,
        }),
      setInteractionType: (type) =>
        mediaUserEvents$.next({
          __id: "SET_INTERACTION_TYPE",
          type,
        }),
    },
  };
};

export default MediaStore;
